import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import {
  TrophyIcon,
  HandHeartIcon,
  SparkleIcon,
  CrownIcon,
  MedalIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

const RANK_BADGES = [
  {
    icon: CrownIcon,
    label: "#1 FOUNDER",
    ring: "ring-amber-400/60",
    bg: "bg-gradient-to-br from-amber-300 to-amber-500",
    fg: "text-zinc-900",
    size: 24,
  },
  {
    icon: MedalIcon,
    label: "#2",
    ring: "ring-zinc-300/60",
    bg: "bg-gradient-to-br from-zinc-200 to-zinc-400",
    fg: "text-zinc-900",
    size: 20,
  },
  {
    icon: MedalIcon,
    label: "#3",
    ring: "ring-orange-400/50",
    bg: "bg-gradient-to-br from-orange-300 to-orange-500",
    fg: "text-zinc-900",
    size: 20,
  },
];

function RankBadge({ rank }) {
  const cfg = RANK_BADGES[rank];
  if (!cfg) {
    return (
      <div className="h-10 w-10 rounded-full bg-zinc-100 grid place-items-center flex-shrink-0 ring-1 ring-zinc-200">
        <span className="font-mono font-bold text-zinc-500 text-sm">#{rank + 1}</span>
      </div>
    );
  }
  const { icon: Icon, bg, fg, size, ring } = cfg;
  return (
    <div
      className={`h-10 w-10 rounded-full grid place-items-center flex-shrink-0 ring-2 ${ring} ${bg}`}
    >
      <Icon size={size} weight="fill" className={fg} />
    </div>
  );
}

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/referral/leaderboard?limit=10")
      .then((r) => alive && setItems(r.data.items || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const totalSignups = items.reduce((a, b) => a + (b.referred_signups || 0), 0);
  const totalEarned = items.reduce((a, b) => a + (b.earnings_usd || 0), 0);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="referrals-page">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="pill bg-[#1D78AB] text-white flex items-center gap-1">
            <HandHeartIcon size={11} weight="fill" /> REFERRAL LEADERBOARD
          </span>
          <span className="pill bg-amber-400 text-zinc-900 flex items-center gap-1">
            <SparkleIcon size={11} weight="fill" /> EARN 2% PER INVITE
          </span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          The Founders <br className="sm:hidden" />
          pulling Bidorah forward.
        </h1>
        <p className="text-zinc-600 mt-4 max-w-2xl leading-relaxed">
          Every approved Founder Seller gets a unique invite link. They earn <b>2%</b> of each
          referred buyer's first paid order — released instantly to their payout ledger.
          Here's who's leading the charge.
        </p>

        {/* Summary */}
        <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">RANKED</p>
            <p className="font-display font-black text-2xl mt-1">{items.length}</p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">SIGNUPS</p>
            <p className="font-display font-black text-2xl mt-1 text-[#1D78AB]">{totalSignups}</p>
          </div>
          <div className="rounded-2xl border border-amber-400 bg-gradient-to-br from-amber-300 to-amber-500 p-4">
            <p className="text-[10px] font-mono tracking-widest text-zinc-900/80 uppercase">
              PAID OUT
            </p>
            <p className="font-display font-black text-2xl mt-1 text-zinc-900">
              ${totalEarned.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="mt-10" data-testid="leaderboard-list">
          {loading ? (
            <p className="text-sm text-zinc-500">Loading leaderboard…</p>
          ) : items.length === 0 ? (
            <div className="border border-zinc-200 rounded-2xl p-12 text-center bg-white">
              <TrophyIcon size={36} className="mx-auto text-zinc-300" />
              <p className="font-display font-bold text-2xl mt-4">
                No referrals yet — be the first.
              </p>
              <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">
                Apply as a Founder Seller, share your invite link, and you'll be seeded here as
                your signups land.
              </p>
              <Link
                to="/apply-to-sell"
                className="inline-flex items-center gap-1 mt-5 text-sm font-semibold text-[#1D78AB] hover:underline"
                data-testid="leaderboard-apply-cta"
              >
                Apply for a Founder seat <ArrowRightIcon size={14} />
              </Link>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-2xl bg-white divide-y divide-zinc-100 overflow-hidden">
              {items.map((row, i) => (
                <div
                  key={row.referral_code}
                  className={`p-4 sm:p-5 flex items-center gap-4 ${
                    i === 0 ? "bg-gradient-to-r from-amber-50 to-white" : ""
                  }`}
                  data-testid={`leaderboard-row-${row.referral_code}`}
                >
                  <RankBadge rank={i} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-base sm:text-lg truncate">
                        {row.brand_name}
                      </p>
                      <span
                        className={`pill ${
                          row.segment === "wholesale"
                            ? "bg-amber-500 text-white"
                            : "bg-[#1D78AB] text-white"
                        }`}
                      >
                        {row.segment}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-zinc-500 mt-0.5 truncate">
                      {row.referral_code}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-display font-black text-xl sm:text-2xl text-emerald-600 tracking-tight">
                      ${Number(row.earnings_usd || 0).toFixed(2)}
                    </p>
                    <p className="text-[11px] text-zinc-500 font-medium">
                      {row.referred_signups} signup{row.referred_signups === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="mt-10 rounded-3xl border-2 border-zinc-900 bg-zinc-900 text-white p-6 sm:p-8">
          <p className="pill bg-amber-400 text-zinc-900 mb-3">WANT YOUR NAME HERE?</p>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
            Apply as a Founder Seller.
          </h3>
          <p className="text-sm text-white/70 mt-2 max-w-xl">
            Approval unlocks a unique invite code + revenue-share on every referred buyer's
            first purchase. No cap on earnings, no expiry on your code.
          </p>
          <Link
            to="/apply-to-sell"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-900 font-semibold px-5 py-2.5 text-sm transition-colors"
            data-testid="leaderboard-bottom-cta"
          >
            <SparkleIcon size={14} weight="fill" />
            Apply for a Founder seat
            <ArrowRightIcon size={14} />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
